import { useEffect, useMemo } from 'react';
import _debounce from 'lodash/debounce';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';
import { getOrganizationClientRecordsTableSettingsCacheKey } from './cacheKeys';
import { useOrganizationId } from './useOrganization';

const SAVE_DEBOUNCE_MS = 500;

export type UseOrganizationClientRecordsTableSettingsReturnValue = {
    tableSettings: APITypes.organizationService.GetOrganizationClientRecordsTableSettingsResponse | undefined;
    isLoadingTableSettings: boolean;
    upsertTableSettings: (
        settings: APITypes.organizationService.UpsertOrganizationClientRecordsTableSettingsRequest,
    ) => void;
};

export default function useOrganizationClientRecordsTableSettings(): UseOrganizationClientRecordsTableSettingsReturnValue {
    const organizationId = useOrganizationId();
    const queryClient = useQueryClient();

    const tableSettingsQuery = useQuery({
        queryKey: getOrganizationClientRecordsTableSettingsCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationClientRecordsTableSettingsResponse>(
                urls.api.organizations.clientRecords.tableSettings(organizationId),
                { ignoreErrorStatus: [404] },
            );
        },
    });

    const upsertTableSettingsMutation = useMutation({
        mutationFn: async (
            settings: APITypes.organizationService.UpsertOrganizationClientRecordsTableSettingsRequest,
        ) => {
            return await appAPI.put<APITypes.organizationService.UpsertOrganizationClientRecordsTableSettingsResponse>(
                urls.api.organizations.clientRecords.tableSettings(organizationId),
                { body: settings },
            );
        },
        onSuccess: (data) => {
            queryClient.setQueryData(getOrganizationClientRecordsTableSettingsCacheKey(), data);
        },
    });

    const upsertTableSettingsDebounced = useMemo(
        () =>
            _debounce((settings: APITypes.organizationService.UpsertOrganizationClientRecordsTableSettingsRequest) => {
                upsertTableSettingsMutation.mutate(settings);
            }, SAVE_DEBOUNCE_MS),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    useEffect(() => {
        return () => {
            upsertTableSettingsDebounced.cancel();
        };
    }, [upsertTableSettingsDebounced]);

    return {
        tableSettings: tableSettingsQuery.data,
        isLoadingTableSettings: tableSettingsQuery.isLoading,
        upsertTableSettings: upsertTableSettingsDebounced,
    };
}
